import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { LuDownload, LuX } from "react-icons/lu";

const ICON_BUTTON =
    "flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-primary)] bg-white text-primary-700 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:shadow-[var(--ring)]";

function download(src, name) {
    const link = document.createElement("a");
    link.href = src;
    link.download = name || "tryon-image.png";
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/** Full-screen image or video viewer. Esc, the close button, or a click outside the media closes it. */
export default function MediaLightbox({ src, name, kind, onClose }) {
    const dialogRef = useRef(null);
    const open = Boolean(src);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        if (!open && dialog.open) dialog.close();
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    return createPortal(
        <dialog
            ref={dialogRef}
            aria-label={name || (kind === "video" ? "Video preview" : "Image preview")}
            className="tryon-agent fixed inset-0 z-[1100] m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-[rgba(16,16,15,0.94)] open:flex open:flex-col"
            onClose={onClose}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            {open && (
                <>
                    <div className="flex shrink-0 items-center justify-between gap-4 px-5 py-4">
                        <p className="min-w-0 truncate font-Mulish text-sm text-white/75">{name}</p>
                        <div className="flex shrink-0 items-center gap-3">
                            <button type="button" className={ICON_BUTTON} onClick={() => download(src, name)} aria-label={kind === "video" ? "Download video" : "Download image"} title="Download">
                                <LuDownload size={18} />
                            </button>
                            <button type="button" className={ICON_BUTTON} onClick={onClose} aria-label="Close" title="Close (Esc)" autoFocus>
                                <LuX size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-1 cursor-zoom-out items-center justify-center px-5 pb-6" onClick={onClose}>
                        {kind === "video" ? (
                            <video
                                src={src}
                                controls
                                autoPlay
                                playsInline
                                className="max-h-full max-w-full cursor-default rounded-2xl bg-black"
                                onClick={(event) => event.stopPropagation()}
                            />
                        ) : (
                            <img
                                src={src}
                                alt={name}
                                className="max-h-full max-w-full cursor-default rounded-2xl object-contain"
                                onClick={(event) => event.stopPropagation()}
                            />
                        )}
                    </div>
                </>
            )}
        </dialog>,
        document.body
    );
}
